import React, { useState, useEffect, useRef } from 'react';
import useHotStampingChoices from 'components/quote_builder_widget/hooks/useHotStampingChoices';

const MILLIMETRES_PER_INCH = 25.4;

// The visitor's hot stamping (yaldız): one hot stamping material from the owner's list, or none; with one, the hot
// stamping area's width and height in the form's unit and -- on a dieline of several parts -- the part it goes on.
// Posts the fields QuoteBuilderWidget::SubmitQuoteService reads. Nothing renders when the owner offers no hot stamping
// material on the chosen carton material.
export const useHotStampingChoice = (widgetIdentifier, cartonMaterialUserPriceId, drawingId, unit) => {
  const hotStamping = useHotStampingChoices(widgetIdentifier, cartonMaterialUserPriceId, drawingId);
  const [hotStampingMaterialId, setHotStampingMaterialId] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [partCode, setPartCode] = useState('');
  const unitRef = useRef(unit);

  // The area is posted in the form's unit, so a unit switch converts what the visitor entered.
  useEffect(() => {
    if (unitRef.current === unit) { return; }
    const factor = unit === 'in' ? 1 / MILLIMETRES_PER_INCH : MILLIMETRES_PER_INCH;
    const convert = (length) => (length === '' ? '' : String(Math.round(Number(length) * factor * 100) / 100));
    setWidth(convert);
    setHeight(convert);
    unitRef.current = unit;
  }, [unit]);

  if (hotStamping === null || hotStamping.hotStampingMaterials.length === 0) {
    return { offered: false };
  }

  const { hotStampingMaterials, parts } = hotStamping;
  const offeredIds = hotStampingMaterials.map((hotStampingMaterial) => String(hotStampingMaterial.id));
  const selectedHotStampingMaterialId = offeredIds.includes(String(hotStampingMaterialId)) ? String(hotStampingMaterialId) : '';
  const selectedPartCode = parts.some((part) => part.code === partCode) ? partCode : (parts[0] ? parts[0].code : '');

  return {
    offered: true,
    hotStampingMaterials,
    parts,
    selectedHotStampingMaterialId,
    selectedPartCode,
    width,
    height,
    onHotStampingMaterialChange: (event) => { setHotStampingMaterialId(event.target.value); },
    onWidthChange: (event) => { setWidth(event.target.value); },
    onHeightChange: (event) => { setHeight(event.target.value); },
    onPartChange: (event) => { setPartCode(event.target.value); },
  };
};

// Wrapper goes around the fields only when hot stamping is offered, so a theme's island never renders empty.
const BaseHotStampingChooser = ({ widgetIdentifier, cartonMaterialUserPriceId, drawingId, unit, styles, selectClassName, lengthInputClassName, Wrapper = React.Fragment }) => {
  const choice = useHotStampingChoice(widgetIdentifier, cartonMaterialUserPriceId, drawingId, unit);

  if (!choice.offered) {
    return null;
  }

  const lengthField = (name, label, value, onChange) => (
    <div className={styles.formGroup} data-widget-element="form-group">
      <label className={styles.label} data-widget-element="label">{label}</label>
      <div className={styles.inputWrapper} data-widget-element="input-wrapper">
        <input
          type="number"
          name={name}
          min="0"
          step="any"
          required
          value={value}
          onChange={onChange}
          className={lengthInputClassName}
          data-widget-element="input"
        />
        <span className={styles.unitSuffix} data-widget-element="unit-suffix">{unit}</span>
      </div>
    </div>
  );

  return (
    <Wrapper>
      <div className={styles.formGroup} data-widget-element="form-group">
        <label className={styles.label} data-widget-element="label">{t('quote_builder_widget.hot_stamping')}</label>
        <div className={styles.inputWrapper}>
          <select
            name="hot_stamping_material_user_price_id"
            value={choice.selectedHotStampingMaterialId}
            onChange={choice.onHotStampingMaterialChange}
            className={selectClassName}
            data-widget-element="select"
          >
            <option value="">{t('quote_builder_widget.hot_stamping_none')}</option>
            {choice.hotStampingMaterials.map((hotStampingMaterial) => (
              <option key={hotStampingMaterial.id} value={hotStampingMaterial.id}>{hotStampingMaterial.label}</option>
            ))}
          </select>
        </div>
      </div>
      {choice.selectedHotStampingMaterialId !== '' && (
        <>
          {lengthField('hot_stamping_width', t('quote_builder_widget.hot_stamping_width'), choice.width, choice.onWidthChange)}
          {lengthField('hot_stamping_height', t('quote_builder_widget.hot_stamping_height'), choice.height, choice.onHeightChange)}
          {choice.parts.length > 1 && (
            <div className={styles.formGroup} data-widget-element="form-group">
              <label className={styles.label} data-widget-element="label">{t('quote_builder_widget.hot_stamping_part')}</label>
              <div className={styles.inputWrapper}>
                <select
                  name="hot_stamping_part_code"
                  value={choice.selectedPartCode}
                  onChange={choice.onPartChange}
                  className={selectClassName}
                  data-widget-element="select"
                >
                  {choice.parts.map((part) => <option key={part.code} value={part.code}>{part.name}</option>)}
                </select>
              </div>
            </div>
          )}
          {choice.parts.length === 1 && <input type="hidden" name="hot_stamping_part_code" value={choice.selectedPartCode} />}
        </>
      )}
    </Wrapper>
  );
};

export default BaseHotStampingChooser;
